import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Landmark, PiggyBank, HandCoins, Sparkles } from 'lucide-react';

/**
 * Phase 84.27 v3.6.0 — Goals onboarding modal.
 *
 * Fires when the user lands on /plan-ahead?tab=goals AND has zero active
 * goals. Per product spec: fires EVERY time they land in that state
 * (not once-per-user), because the intent is a gentle re-orientation
 * whenever the shelf is empty — not a permanent dismiss.
 */
export default function GoalsOnboardingModal({ open, onOpenChange, onCreate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="goals-onboarding-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl leading-tight">
            Goals that keep themselves on track
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1 text-sm text-muted-foreground leading-relaxed">
          <p>
            Tell us what you&rsquo;re working toward and where the money lives. We&rsquo;ll automatically track your progress, so there&rsquo;s less to update and more to look forward to.
          </p>
          <div className="space-y-3">
            <div className="flex gap-3 items-start" data-testid="onboarding-example-auto">
              <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 shrink-0">
                <PiggyBank className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">Automatically tracked</div>
                <div className="text-xs">
                  Link a savings account, investment, or retirement account, and we&rsquo;ll keep your progress current.
                </div>
              </div>
            </div>
            <div className="flex gap-3 items-start" data-testid="onboarding-example-maintain">
              <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 shrink-0">
                <Landmark className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">Maintain a minimum balance</div>
                <div className="text-xs">
                  Set a safety-net amount for an account, such as your emergency fund, and stay on top of it.
                </div>
              </div>
            </div>
            <div className="flex gap-3 items-start" data-testid="onboarding-example-debt">
              <div className="mt-0.5 rounded-full bg-primary/10 p-1.5 shrink-0">
                <HandCoins className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="font-medium text-foreground">Pay off debt</div>
                <div className="text-xs">
                  Link a loan or liability to see your payoff progress update automatically.
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs italic pt-1">
            Prefer to update things yourself? Every goal can also be tracked manually.
          </p>
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="goals-onboarding-skip"
          >
            Maybe later
          </Button>
          <Button
            onClick={() => { onOpenChange(false); onCreate?.(); }}
            data-testid="goals-onboarding-create"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            Create a goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
